import { useMemo } from 'react'
import { CITY_HINT_CODES } from 'src/constants/cityHintCodes'
import useUserLocation from 'src/hooks/useUserLocation'

/**
 * Returns the compact 2-5 char city code (`mty`, `cdmx`, `spgg`, etc.)
 * derived from the user's browser geo or IP lookup — or `''` when no
 * location signal is available.
 *
 * Consumed by `appendCityHint` in `src/utils/whatsapp.ts` to suffix
 * bare-CTA WhatsApp prefill messages as `… · {code}`. The codes match
 * existing Mexican abbreviation conventions (`mty` = Monterrey,
 * `cdmx` = Ciudad de México, `spgg` = San Pedro Garza García) so the
 * suffix reads as a city marker rather than tracking garbage in the
 * customer's draft.
 *
 * Returns `''` (NOT undefined) when:
 *  - both browser geo AND IP lookup are absent (no signal)
 *  - the SSR pass renders before client hydration (consent state false)
 *  - the resolved SearchPageCity is missing from the code map (defensive)
 *
 * Returns the lowercase code when at least one of `browserLocation` /
 * `ipCenter` resolved. The LangGraph analysis prompt is updated alongside
 * this hook to decode `· {code}` at end-of-message via a mirrored table
 * on the server (`spotme-server/src/constants/cityHintCodes.ts`), and to
 * treat the resolved city as an approximate hint, not a confirmed claim.
 */
export const useWhatsappCityHint = (): string => {
  const { closestCity, browserLocation, ipCenter } = useUserLocation()

  return useMemo(() => {
    const hasSignal = !!browserLocation || !!ipCenter
    if (!hasSignal || !closestCity) return ''
    return CITY_HINT_CODES[closestCity] ?? ''
  }, [closestCity, browserLocation, ipCenter])
}

export default useWhatsappCityHint
